import { GuidedField } from '../../guidance/GuidedField';
import { fieldDef } from '../../guidance/roleFieldRegistry';
import { splitLines, type RoleFormKey } from '../../roleForm';
import { GroupedCombobox } from '../guided/GroupedCombobox';
import { MultiSelectCombobox } from '../guided/MultiSelectCombobox';
import { customCreator, referenceLoader, useKnownOptions } from '../guided/referenceOptions';
import { useCategoryNames } from './useCategoryNames';
import { useWizard } from './WizardContext';

/**
 * The field kinds the six steps are made of. Each takes only a registry key: the label, badge, guidance, limits and the option category all come
 * from roleFieldRegistry.ts, and the value lives in the wizard's form. No option list is written here: options come from the reference-options API.
 */

export interface Choice { value: string; label: string }

const wide = (isWide?: boolean) => (isWide ? 'jr-grid__wide' : undefined);

export function TextField({ k, isWide, hint }: { k: RoleFormKey; isWide?: boolean; hint?: string }) {
  const w = useWizard();
  const def = fieldDef(k);
  const max = def.validation.maxLength;
  return (
    <GuidedField field={def} error={w.errorOf(k)} className={wide(isWide)} hint={hint} counter={max && max <= 500 ? { length: w.form[k].length, max } : undefined}>
      {(a) => <input {...a} type="text" autoComplete="off" value={w.form[k]} disabled={w.disabled} onChange={(e) => w.set(k, e.target.value)} />}
    </GuidedField>
  );
}

export function TextAreaField({ k, rows = 3, isWide, children }: { k: RoleFormKey; rows?: number; isWide?: boolean; children?: React.ReactNode }) {
  const w = useWizard();
  const def = fieldDef(k);
  const max = def.validation.maxLength;
  return (
    <div className={wide(isWide)}>
      <GuidedField field={def} error={w.errorOf(k)} counter={max ? { length: w.form[k].length, max } : undefined}>
        {(a) => <textarea {...a} rows={rows} value={w.form[k]} disabled={w.disabled} onChange={(e) => w.set(k, e.target.value)} />}
      </GuidedField>
      {children}
    </div>
  );
}

/** A list written one item per line (alternate titles, expected outcomes, compliance responsibilities). */
export function LinesField({ k, rows = 3, isWide }: { k: RoleFormKey; rows?: number; isWide?: boolean }) {
  const w = useWizard();
  const def = fieldDef(k);
  return (
    <GuidedField field={def} error={w.errorOf(k)} className={wide(isWide)} hint="One per line.">
      {(a) => <textarea {...a} rows={rows} value={w.form[k]} disabled={w.disabled} onChange={(e) => w.set(k, e.target.value)} />}
    </GuidedField>
  );
}

/** A short fixed list (Yes / No / Not stated, the employment types). A saved value that is not in the list is kept as an extra option. */
export function ChoiceField({ k, choices, isWide, notStated = 'Not stated' }: { k: RoleFormKey; choices: readonly Choice[]; isWide?: boolean; notStated?: string }) {
  const w = useWizard();
  const def = fieldDef(k);
  const stored = w.form[k];
  const known = choices.some((c) => c.value.toLowerCase() === stored.toLowerCase());
  return (
    <GuidedField field={def} error={w.errorOf(k)} className={wide(isWide)}>
      {(a) => (
        <select {...a} value={stored} disabled={w.disabled} onChange={(e) => w.set(k, e.target.value)}>
          <option value="">{notStated}</option>
          {choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          {stored && !known && <option value={stored}>{stored}</option>}
        </select>
      )}
    </GuidedField>
  );
}

/** A controlled list: choose one of the options, in order, never your own value (the career level, the FLSA classification). */
export function ReferenceSelectField({ k, isWide }: { k: RoleFormKey; isWide?: boolean }) {
  const w = useWizard();
  const def = fieldDef(k);
  const stored = w.form[k];
  const { names, failed } = useCategoryNames(def.optionCategory!);
  const known = names?.some((n) => n.toLowerCase() === stored.toLowerCase()) ?? true;
  return (
    <GuidedField
      field={def} error={w.errorOf(k)} className={wide(isWide)}
      hint={failed ? 'The list of options could not be loaded. What is saved is kept.' : stored && !known ? 'Custom (kept as entered): this value is not one of the EPN options. It stays as it is until you choose another.' : undefined}
    >
      {(a) => (
        <select {...a} value={stored} disabled={w.disabled} onChange={(e) => w.set(k, e.target.value)}>
          <option value="">Not set</option>
          {(names ?? []).map((n) => <option key={n} value={n}>{n}</option>)}
          {stored && !(names ?? []).some((n) => n.toLowerCase() === stored.toLowerCase()) && <option value={stored}>{names && !known ? `${stored} (kept as entered)` : stored}</option>}
        </select>
      )}
    </GuidedField>
  );
}

/** One value chosen from a reference category (education, experience, travel, family ...). Your own value is allowed after a confirmation. */
export function ReferenceComboField({ k, isWide }: { k: RoleFormKey; isWide?: boolean }) {
  const w = useWizard();
  const def = fieldDef(k);
  const category = def.optionCategory!;
  const value = w.form[k];
  const isKnown = useKnownOptions(category, value ? [value] : []);
  const known = value ? isKnown(value) : undefined;
  return (
    <GuidedField field={def} error={w.errorOf(k)} className={wide(isWide)}>
      {(a) => (
        <GroupedCombobox
          a11y={a}
          mode="select"
          value={value}
          valueLabel={value}
          disabled={w.disabled}
          load={referenceLoader(category, w.suggestionTemplate?.id)}
          onSelect={(o) => w.set(k, o.label)}
          onClear={() => w.set(k, '')}
          clearLabel={def.label}
          allowCustom={def.customValuePolicy !== 'none'}
          confirmCustom={def.customValuePolicy === 'allowedWithConfirm'}
          onCustom={customCreator(category)}
          keptNote={value && known === false ? 'Custom (kept as entered): this is not one of the EPN options. It stays as it is until you change it.' : null}
          placeholder="Search the options"
        />
      )}
    </GuidedField>
  );
}

/** Several values chosen from a reference category. Values stored as lines of text; an old value that matches no option stays as a "custom" chip. */
export function ReferenceMultiField({ k, noun, isWide, hint }: { k: RoleFormKey; noun: string; isWide?: boolean; hint?: string }) {
  const w = useWizard();
  const def = fieldDef(k);
  const category = def.optionCategory!;
  const values = splitLines(w.form[k]);
  const isKnown = useKnownOptions(category, values);
  return (
    <GuidedField field={def} error={w.errorOf(k)} className={wide(isWide)} labelMode="control" hint={hint}>
      {(a) => (
        <MultiSelectCombobox
          a11y={a}
          values={values}
          onChange={(next) => w.set(k, next.join('\n'))}
          load={referenceLoader(category, w.suggestionTemplate?.id)}
          allowCustom={def.customValuePolicy !== 'none'}
          confirmCustom={def.customValuePolicy === 'allowedWithConfirm'}
          onCustom={customCreator(category)}
          isKnown={isKnown}
          noun={noun}
          disabled={w.disabled}
          maxItems={def.validation.maxItems}
        />
      )}
    </GuidedField>
  );
}
